import { Button, Tag, Typography } from "@hellocarbo/lasagne";
import { ArrowLeft } from "lucide-react";
import { useContext } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  data,
  LoaderFunctionArgs,
  useLoaderData,
  useParams,
} from "react-router";
import invariant from "tiny-invariant";

import DetailledReport from "@/components/ImportDeposit/DetailledReport";
import FileInformation from "@/components/ImportDeposit/FileInformation";
import Card from "@/components/ui/Card";
import PageWrapper from "@/components/ui/PageWrapper";
import { FeaturesContext } from "@/contexts";
import { TImportDeposit, TMatchingResponse } from "@/types";
import { fromImportStatusToIntent } from "@/utils/importDeposits";

export async function loader({ params }: LoaderFunctionArgs) {
  const { scoreId, importId } = params;
  invariant(scoreId, "No scoreId found!");
  invariant(importId, "No importId found!");

  const importDepositResponse = await fetch(
    `/api/client/scores/${scoreId}/import_deposits/${importId}/`
  );
  if (importDepositResponse.status !== 200) {
    throw data("Failed to load this Import Deposit", {
      status: importDepositResponse.status,
    });
  }

  const { data: importDeposit } = (await importDepositResponse.json()) as {
    data: TImportDeposit;
  };

  let matchings: Array<TMatchingResponse> = [];
  // As the "import_deposit_view_react" feature flag and the "import_to_verify_interface" might be rolled out separately
  // we will for now make this call, even though it can be redondant with the one made in ImportDepositMatching.tsx
  if (importDeposit.status === "matching") {
    const importDepositMatchingResponse = await fetch(
      `/api/client/scores/${scoreId}/import_deposits/${importId}/matching_entries`
    );
    if (importDepositMatchingResponse.status !== 200) {
      throw data("Failed to load the matching for this Import Deposit", {
        status: importDepositMatchingResponse.status,
      });
    }
    const { data: responseMatchings } =
      (await importDepositMatchingResponse.json()) as {
        data: Array<TMatchingResponse>;
      };
    matchings = responseMatchings;
  }

  return { importDeposit, matchings };
}

function ImportDeposit() {
  const { t } = useTranslation("importDeposit");
  const { scoreId, importId } = useParams();
  const { importDeposit, matchings } = useLoaderData<typeof loader>();

  const { featureFlags } = useContext(FeaturesContext);
  if (!featureFlags || !featureFlags["import_deposit_view_react"]) {
    return null;
  }

  return (
    <PageWrapper>
      <div className="mx-xl my-lg min-h-screen">
        <Card className="my-lg flex">
          <div className="space-x-sm flex flex-1 items-center">
            <Button
              as="a"
              href={`/scores/${scoreId}/import_deposits`}
              Icon={ArrowLeft}
              intent="primary"
              shape="soft"
            />
            <Typography as="h1" step="1" weight="bold">
              {importDeposit.input_file}
            </Typography>
          </div>
          <div className="flex items-center justify-end">
            <Tag
              text={t(
                `importDeposit:importDepositStatus.${importDeposit.status}`
              )}
              intent={fromImportStatusToIntent(importDeposit.status)}
            />
          </div>
        </Card>

        <Card className="gap-x-sm space-y-md flex flex-col items-stretch">
          {importDeposit.status === "matching" && (
            /* Rough matching messaging and CTA as this will last only while the Matching feature is rolling out. */
            <div className="p-sm space-y-sm justify-center rounded-md bg-[#FFF9EB]">
              <Typography>
                <Trans
                  t={t}
                  i18nKey="importDeposit:importDetails.overview.matchingInfo"
                  components={{
                    1: <br />,
                  }}
                />
              </Typography>
              <Typography weight="semibold">
                {t(`importDeposit:importDetails.overview.matchingNumber`, {
                  x: matchings.length,
                })}
              </Typography>
              <div className="flex justify-center">
                <Button
                  intent="primary"
                  as="a"
                  href={`/scores/${scoreId}/import_deposits/${importId}/matching`}
                >
                  {t("importDeposit:importDetails.overview.matchingCta")}
                </Button>
              </div>
            </div>
          )}
          <div className="gap-x-sm flex w-full flex-row items-start">
            <DetailledReport importDeposit={importDeposit} />
            <FileInformation importDeposit={importDeposit} />
          </div>
        </Card>
      </div>
    </PageWrapper>
  );
}

export default ImportDeposit;
